import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Table, Tag, Space, Button, Form, Row, Col, InputNumber, Select, message, Modal, Input, DatePicker, Switch } from 'antd';
import { useNavigate } from 'react-router-dom';
import request from '../../../utils/request';
import { getCustomerList, getProjectList } from '../../../services/project';

const { Option } = Select;

const BillingRulesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [tierLoading, setTierLoading] = useState(false);
  const [currentRule, setCurrentRule] = useState<any | null>(null);
  const [tiers, setTiers] = useState<any[]>([]);
  const [tierForm] = Form.useForm();
  const [editTier, setEditTier] = useState<any | null>(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [ruleForm] = Form.useForm();
  const [ruleSaving, setRuleSaving] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [projectLoading, setProjectLoading] = useState(false);

  const getJSON = async (url: string, config?: any) => {
    const resp = await request.get(url, config);
    return resp?.data;
  };

  const fetchData = async () => {
    try {
      const v = form.getFieldsValue();
      const qs = new URLSearchParams();
      if (v.customer_id) qs.set('customer_id', String(v.customer_id));
      if (v.project_id) qs.set('project_id', String(v.project_id));
      setLoading(true);
      const data = await getJSON(`/api/v1/billing/billing-rules/effective/?${qs.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const searchCustomers = async (kw?: string) => {
    setCustomerLoading(true);
    try {
      // 与“只读页”一致：直接调用 getCustomerList({ onlyMain: true }) 返回主账号列表
      const list: any[] = (await getCustomerList({ onlyMain: true })) || [];
      const keyword = String(kw || '').trim().toLowerCase();
      const filtered = keyword
        ? list.filter((c: any) => {
            const sys = String(c?.customer_code || c?.profile?.customer_code || '').toLowerCase();
            const nm = String(c?.name || c?.username || '').toLowerCase();
            return nm.includes(keyword) || sys.includes(keyword);
          })
        : list;
      const opts = filtered.map((c: any) => {
        const sys = c?.customer_code || c?.profile?.customer_code || '';
        const name = c?.name || c?.username || '';
        const label = sys ? `${name} · ${sys}` : name;
        return { value: Number(c.id), label };
      });
      setCustomerOptions(opts);
    } catch {
      setCustomerOptions([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  const searchProjects = async (customerId?: number, kw?: string) => {
    if (!customerId) { setProjectOptions([]); return; }
    setProjectLoading(true);
    try {
      const pageSize = 200;
      let page = 1;
      let all: any[] = [];
      while (true) {
        const resp = await getProjectList({ page, page_size: pageSize, search: kw || '', customer: customerId } as any);
        const results = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : [];
        if (!results || results.length === 0) break;
        all.push(...results);
        if (results.length < pageSize) break;
        page += 1;
        if (page > 1000) break;
      }
      setProjectOptions(all.map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` })));
    } catch {
      setProjectOptions([]);
    } finally {
      setProjectLoading(false);
    }
  };

  const openTierModal = async (rule: any) => {
    setCurrentRule(rule);
    setTierModalOpen(true);
    setTierLoading(true);
    try {
      const data = await getJSON(`/api/v1/billing/billing-rules/${rule.id}/tiers`);
      setTiers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.message || '加载梯度失败');
    } finally {
      setTierLoading(false);
    }
  };

  const submitTier = async () => {
    try {
      const v = await tierForm.validateFields();
      setTierLoading(true);
      if (!currentRule) return;
      if (editTier && editTier.id) {
        // 更新
        await request.patch(`/api/v1/billing/billing-rules/${currentRule.id}/tiers/${editTier.id}`, {
          min_value: v.min_value ?? null,
          max_value: v.max_value ?? null,
          unit_price: v.unit_price,
        });
      } else {
        // 新建
        await request.post(`/api/v1/billing/billing-rules/${currentRule.id}/tiers`, {
          min_value: v.min_value ?? null,
          max_value: v.max_value ?? null,
          unit_price: v.unit_price,
        });
      }
      // 刷新
      const list = await getJSON(`/api/v1/billing/billing-rules/${currentRule.id}/tiers`);
      setTiers(Array.isArray(list) ? list : []);
      setEditTier(null);
      tierForm.resetFields();
      message.success('已保存');
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    } finally {
      setTierLoading(false);
    }
  };

  const delTier = async (row: any) => {
    try {
      if (!currentRule) return;
      setTierLoading(true);
      await request.delete(`/api/v1/billing/billing-rules/${currentRule.id}/tiers/${row.id}`);
      const list = await getJSON(`/api/v1/billing/billing-rules/${currentRule.id}/tiers`);
      setTiers(Array.isArray(list) ? list : []);
      message.success('已删除');
    } catch (e: any) {
      message.error(e?.message || '删除失败');
    } finally {
      setTierLoading(false);
    }
  };

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>费率规则（生效视图）</div>
          <Space>
            <Button onClick={fetchData}>刷新</Button>
          </Space>
        </div>
        <Form form={form} layout="inline" onValuesChange={() => {}}>
          <Form.Item label="所属客户" name="customer_id">
            <Select
              showSearch
              allowClear
              style={{ width: 260 }}
              options={customerOptions}
              loading={customerLoading}
              onFocus={() => searchCustomers('')}
              onSearch={(v) => searchCustomers(v)}
              filterOption={false}
              onChange={(val) => {
                // 切换客户时清空项目
                form.setFieldsValue({ customer_id: val, project_id: undefined });
                setProjectOptions([]);
                if (val) searchProjects(Number(val), '');
              }}
              placeholder="选择客户（可搜索）"
            />
          </Form.Item>
          <Form.Item label="项目" name="project_id">
            <Select
              allowClear
              showSearch
              disabled={!form.getFieldValue('customer_id')}
              style={{ width: 300 }}
              loading={projectLoading}
              options={projectOptions}
              onFocus={() => { const cid = form.getFieldValue('customer_id'); if (cid) searchProjects(Number(cid), ''); }}
              onSearch={(v) => { const cid = form.getFieldValue('customer_id'); if (cid) searchProjects(Number(cid), v); }}
              filterOption={false}
              placeholder={form.getFieldValue('customer_id') ? '选择项目（可搜索）' : '先选择客户'}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={fetchData}>查询</Button>
              <Button onClick={() => { form.resetFields(); fetchData(); }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        <div style={{ height: 12 }} />
        <Table
          size="small"
          rowKey={(r:any, i?: number) => String(r?.id ?? i)}
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          columns={[
            { title: '规则系统编码', dataIndex: 'system_code', width: 200, render: (v:any) => v || '-' },
            { title: '显示名称', dataIndex: 'display_name', width: 220 },
            { title: '操作类型', dataIndex: 'operation_type_name', width: 160 },
            { title: '单位', dataIndex: 'unit_label', width: 160, render: (v:any, r:any) => v || r.billing_unit || '-' },
            { title: '类型', dataIndex: 'rule_type', width: 100, render: (v:any) => <Tag color={v==='tiered'?'blue':'default'}>{v||'-'}</Tag> },
            { title: '单价/区间', key: 'price', width: 160, render: (_:any, r:any) => (r.rule_type === 'tiered' ? (r.unit_price_display || '-') : (r.unit_price ?? '-')) },
            { title: '来源层级', dataIndex: 'source_level', width: 100 },
            { title: '客户后台', dataIndex: 'visible_in_customer_portal', width: 120, render: (v:any) => (v !== false ? <Tag color="blue">可见</Tag> : <Tag>隐藏(仅生效)</Tag>) },
            { title: '操作', key: 'op', width: 340, fixed: 'right', render: (_:any, r:any) => (
              <Space>
                <Button size="small" onClick={() => {
                  const sp = new URLSearchParams();
                  if (r.customer_id) sp.set('customer_id', String(r.customer_id));
                  if (r.project_id) sp.set('project_id', String(r.project_id));
                  // 预填 operation_type：优先使用系统编码，其次后端字段回退；不要传中文名
                  const opCode = (r as any).operation_type_code
                    || (r as any).operation_type_system_code
                    || (r as any)?.operation_type?.system_code
                    || '';
                  if (opCode) sp.set('operation_type', String(opCode));
                  navigate(`/dashboard/admin/billing/rate-preview?${sp.toString()}`);
                }}>去试算</Button>
                <Button size="small" onClick={() => openTierModal(r)}>管理梯度</Button>
                <Button size="small" type="primary" onClick={() => {
                  setCurrentRule(r);
                  setRuleModalOpen(true);
                  // 预填表单
                  ruleForm.setFieldsValue({
                    rule_type: r.rule_type || 'flat',
                    tier_basis: r.tier_basis || 'QUANTITY',
                    pricing_basis: r.pricing_basis || r.tier_basis || 'QUANTITY',
                    quantity_basis: r.quantity_basis || r.tier_basis || 'QUANTITY',
                    unit_label: r.unit_label || undefined,
                    remark: r.rule_remark || undefined,
                    currency: r.currency || 'CNY',
                    unit_price: r.unit_price ?? undefined,
                    effective_from: r.effective_from ? DatePicker as any : undefined,
                    is_active: r.is_active !== false,
                    visible_in_customer_portal: r.visible_in_customer_portal !== false,
                  });
                }}>编辑规则</Button>
              </Space>
            ) },
          ]}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={currentRule ? `管理梯度 - 规则#${currentRule.id}` : '管理梯度'}
        open={tierModalOpen}
        width={800}
        onCancel={() => { setTierModalOpen(false); setCurrentRule(null); setEditTier(null); tierForm.resetFields(); }}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {currentRule?.tier_basis === 'AGE_DAY' && (
            <Alert
              type="warning"
              showIcon
              message="库龄(天)梯度（仓储费）：区间下限含、上限不含，相邻梯度需首尾相接，请写成 0-31、31-61、61-91、91-空(无上限)，不要写成 0-30、31-60，否则第30/60/90天等边界日会因匹配不到梯度而不计费。"
            />
          )}
          <Table
            size="small"
            rowKey={(r:any, i?: number) => String(r?.id ?? i)}
            loading={tierLoading}
            dataSource={tiers}
            pagination={false}
            columns={[
              { title: '下限(含)', dataIndex: 'min_value', width: 120, render: (v:any) => v ?? '0' },
              { title: '上限(不含)', dataIndex: 'max_value', width: 120, render: (v:any) => v ?? '-' },
              { title: '单价', dataIndex: 'unit_price', width: 120 },
              { title: '备注', dataIndex: 'tier_remark', render: (v:any) => v || '-' },
              { title: '操作', key: 'op', width: 160, render: (_:any, row:any) => (
                <Space>
                  <Button size="small" onClick={() => { setEditTier(row); tierForm.setFieldsValue({ min_value: row.min_value, max_value: row.max_value, unit_price: row.unit_price }); }}>编辑</Button>
                  <Button size="small" danger onClick={() => delTier(row)}>删除</Button>
                </Space>
              ) },
            ]}
          />
          <Card size="small" title={editTier ? '编辑梯度' : '新增梯度'}>
            <Form form={tierForm} layout="inline" style={{ gap: 8 }}>
              <Form.Item name="min_value" label="下限(含)"><InputNumber style={{ width: 160 }} /></Form.Item>
              <Form.Item name="max_value" label="上限(不含)"><InputNumber style={{ width: 160 }} /></Form.Item>
              <Form.Item name="unit_price" label="单价" rules={[{ required: true, message: '必填' }]}><InputNumber min={0} style={{ width: 160 }} /></Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" onClick={submitTier} loading={tierLoading}>{editTier ? '保存' : '新增'}</Button>
                  <Button onClick={() => { setEditTier(null); tierForm.resetFields(); }}>清空</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Space>
      </Modal>

      <Modal
        title={currentRule ? `编辑规则 - #${currentRule.id}` : '编辑规则'}
        open={ruleModalOpen}
        onCancel={() => { setRuleModalOpen(false); setCurrentRule(null); ruleForm.resetFields(); }}
        footer={null}
        width={720}
      >
        <Form form={ruleForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="规则类型" name="rule_type">
                <Select>
                  <Option value="flat">固定收费</Option>
                  <Option value="tiered">梯度收费</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="梯度维度" name="tier_basis">
                <Select allowClear>
                  <Option value="QUANTITY">数量</Option>
                  <Option value="WEIGHT_G">重量(克)</Option>
                  <Option value="WEIGHT_KG">重量(千克)</Option>
                  <Option value="VOLUME_CUFT">体积(立方英尺)</Option>
                  <Option value="VOLUME_CBM">体积(立方米)</Option>
                  <Option value="VOLUME_CC">体积(立方厘米)</Option>
                  <Option value="AGE_DAY">库龄(天)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="定价口径（可选）" name="pricing_basis">
                <Select allowClear>
                  <Option value="QUANTITY">数量</Option>
                  <Option value="WEIGHT_G">重量(克)</Option>
                  <Option value="WEIGHT_KG">重量(千克)</Option>
                  <Option value="VOLUME_CUFT">体积(立方英尺)</Option>
                  <Option value="VOLUME_CBM">体积(立方米)</Option>
                  <Option value="VOLUME_CC">体积(立方厘米)</Option>
                  <Option value="AGE_DAY">库龄(天)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="计费数量口径（可选）" name="quantity_basis">
                <Select allowClear>
                  <Option value="QUANTITY">数量</Option>
                  <Option value="WEIGHT_G">重量(克)</Option>
                  <Option value="WEIGHT_KG">重量(千克)</Option>
                  <Option value="VOLUME_CUFT">体积(立方英尺)</Option>
                  <Option value="VOLUME_CBM">体积(立方米)</Option>
                  <Option value="VOLUME_CC">体积(立方厘米)</Option>
                  <Option value="AGE_DAY">库龄(天)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="展示单位(可选)" name="unit_label"><Input placeholder="如 张/箱/次" /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="货币" name="currency"><Select>
                <Option value="CNY">CNY</Option>
                <Option value="USD">USD</Option>
                <Option value="EUR">EUR</Option>
              </Select></Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="固定单价(仅固定收费)" name="unit_price"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="是否启用" name="is_active" valuePropName="checked"><Switch /></Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="客户后台可见" name="visible_in_customer_portal" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label="规则备注" name="remark"><Input.TextArea rows={3} /></Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" loading={ruleSaving} onClick={async () => {
              try {
                const v = await ruleForm.validateFields();
                if (!currentRule) return;
                setRuleSaving(true);
                const payload: any = {
                  rule_type: v.rule_type,
                  tier_basis: v.rule_type === 'tiered' ? (v.tier_basis || 'QUANTITY') : (v.tier_basis || 'QUANTITY'),
                  pricing_basis: v.pricing_basis || v.tier_basis || 'QUANTITY',
                  quantity_basis: v.quantity_basis || v.tier_basis || 'QUANTITY',
                  unit_label: v.unit_label ?? null,
                  remark: v.remark ?? null,
                  currency: v.currency || 'CNY',
                  is_active: v.is_active !== false,
                  visible_in_customer_portal: v.visible_in_customer_portal !== false,
                };
                if (v.rule_type === 'flat') {
                  if (v.unit_price != null) payload.unit_price = v.unit_price;
                }
                // 清理 undefined
                Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
                await request.patch(`/api/v1/billing/billing-rules/${currentRule.id}/`, payload);
                message.success('已保存');
                setRuleModalOpen(false);
                ruleForm.resetFields();
                fetchData();
              } catch (e: any) {
                if (e?.errorFields) return;
                message.error(e?.message || '保存失败');
              } finally {
                setRuleSaving(false);
              }
            }}>保存</Button>
            <Button onClick={() => { setRuleModalOpen(false); ruleForm.resetFields(); }}>取消</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default BillingRulesPage;


